import { apiService } from "./apiService";

export interface IGetProductsParams {
  search?: string;
  category?: string;
  categoryId?: string;
  subCategory?: string;
  subCategories?: string; // Comma-separated string for multiple subcategories
  styleId?: string; // Comma-separated string for multiple style IDs
  materialId?: string; // Comma-separated string for multiple material IDs
  colorId?: string; // Comma-separated string for multiple color IDs
  artistId?: string; // Comma-separated string for multiple artist IDs
  tags?: string[];
  minPrice?: number;
  maxPrice?: number;
  priceRange?: string; // Comma-separated string for multiple price ranges (e.g., "20-100,101-200,201-500")
  featured?: boolean;
  onSale?: boolean;
  sortBy?: "name" | "price" | "popularity" | "createdAt";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
  collection?: "Canvas" | "DigiVision"
}

export interface IProductFilters {
  categories: string[];
  subCategories: string[];
  priceRanges: Array<{
    label: string;
    min: number;
    max?: number;
  }>;
  tags?: string[];
}

export interface IProductSearchParams {
  search?: string;
  categoryId?: string;
  materialId?: string; // Comma-separated string for multiple material IDs
  styleId?: string; // Comma-separated string for multiple style IDs
  colorId?: string; // Comma-separated string for multiple color IDs
  artistId?: string; // Comma-separated string for multiple artist IDs
  minPrice?: number;
  maxPrice?: number;
  priceRange?: string; // Comma-separated string for multiple price ranges (e.g., "20-100,101-200,201-500")
  featured?: boolean;
  isArchived?: boolean;
  sortBy?: "name" | "price" | "popularity" | "createdAt" | "averageRating";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface IPaginatedResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export const productApi = {
  /**
   * Get products with filtering and pagination
   */
  getProducts: async (params: IProductSearchParams | IGetProductsParams = {}) => {
    try {
      return await apiService.get("/products", {
        params,
      });
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  },

  /**
   * Get product by ID
   */
  getProductById: async (productId: string) => {
    try {
      return await apiService.get(`/products/${productId}`);
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);
      throw error;
    }
  },

  /**
   * Get product filters
   */
  getProductFilters: async () => {
    try {
      return await apiService.get("/products/filters");
    } catch (error) {
      console.error("Error fetching product filters:", error);
      throw error;
    }
  },

  /**
   * Get featured products
   */
  getFeaturedProducts: async (limit: number = 10) => {
    try {
      return await apiService.get("/products/featured", {
        params: { limit },
      });
    } catch (error) {
      throw error;
    }
  },

  // Get products on sale
  getSaleProducts: async (limit: number = 6) => {
    try {
      return await apiService.get("/products/sale", {
        params: { limit },
      });
    } catch (error) {
      throw error;
    }
  },

  // Get top rated products
  getTopRatedProducts: async (limit: number = 6) => {
    try {
      return await apiService.get("/products/top-rated", {
        params: { limit },
      });
    } catch (error) {
      throw error;
    }
  },

  getRelatedProducts: async (productId: string, limit: number = 5) => {
    try {
      return await apiService.get(`/products/${productId}/related`, {
        params: { limit },
      });
    } catch (error) {
      console.error(`Error fetching related products for ${productId}:`, error);
      throw error;
    }
  },

  getMostSellingProducts: async (
    timeRange: "week" | "month" | "year" | "all" = "all",
  ) => {
    try {
      return await apiService.get("/products/most-selling", {
        params: {
          timeRange,
        },
      });
    } catch (error) {
      console.error("Error fetching most selling products:", error);
      throw error;
    }
  },

  getProductsByArtistId: async (
    artistId: string,
    page: number = 1,
    limit: number = 10,
    sortBy:
      | "createdAt"
      | "updatedAt"
      | "price"
      | "popularity"
      | "rating" = "createdAt",
    sortOrder: "asc" | "desc" = "desc",
  ) => {
    try {
      return await apiService.get(`/products/artist/${artistId}`, {
        params: {
          page,
          limit,
          sortBy,
          sortOrder,
        },
      });
    } catch (error) {
      console.error(`Error fetching products by artist ID ${artistId}:`, error);
      throw error;
    }
  },
};
